import { MdClose } from "react-icons/md";
import generatePDF, { Resolution, Margin, Options } from "react-to-pdf";

import {
  AlertDialog,
  AlertDialogContent,
} from "@/components/ui/alert-dialog/alert-dialog";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/table";
import SaasakiLogo from "/saasaki_logo.png";

import { Button } from "@/components/ui/button/button";
import { useSalarySlipModal } from "./use-salary-slip-modal";
import { useAuth } from "@/lib/use-auth";
import { useState } from "react";
import { toast } from "sonner";

export const SalarySlipModal = () => {
  const { data } = useAuth();

  const { open, onOpenChange, salary } = useSalarySlipModal();

  const [loading, setLoading] = useState(false);

  const options: Options = {
    filename: "Salary Slip.pdf",
    method: "save",
    // default is Resolution.MEDIUM = 3, which should be enough, higher values
    // increases the image quality but also the size of the PDF, so be careful
    // using values higher than 10 when having multiple pages generated, it
    // might cause the page to crash or hang.
    resolution: Resolution.HIGH,
    page: {
      // margin is in MM, default is Margin.NONE = 0
      margin: Margin.SMALL,
      // default is 'A4'
      format: "letter",
      // default is 'portrait'
      orientation: "landscape",
    },
    canvas: {
      // default is 'image/jpeg' for better size performance
      mimeType: "image/jpeg",
      qualityRatio: 1,
    },
    // Customize any value passed to the jsPDF instance and html2canvas
    // function. You probably will not need this and things can break,
    // so use with caution.
    overrides: {
      // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
      pdf: {
        compress: true,
      },
      // see https://html2canvas.hertzen.com/configuration for more options
      canvas: {
        useCORS: true,
      },
    },
  };

  // you can also use a function to return the target element besides using React refs
  const getTargetElement = () => document.getElementById("pdf-container");

  const downloadPdf = async () => {
    setLoading(true);
    const loadinToast = toast.loading("Pdf procesessing");

    try {
      await generatePDF(getTargetElement, options);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setLoading(false);
      toast.dismiss(loadinToast);
    }
  };

  const earningComponents = salary?.components?.filter(
    (com) => com.type === "Earning"
  );
  const deductionComponents = salary?.components?.filter(
    (com) => com.type === "Deduction"
  );

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="p-0 max-w-[90%]">
          <div className="max-h-[700px] overflow-y-auto">
            <div className="border-b px-3 py-2 flex items-center justify-between">
              <h3 className="text-xl font-medium tracking-normal">
                Salary Slip
              </h3>
              <div className="relative flex items-center gap-4">
                <button
                  type="button"
                  className="p-1 rounded-md transition-all duration-300 hover:bg-accent"
                  onClick={() => {
                    onOpenChange(false);
                    // form.reset();
                  }}
                >
                  <MdClose className="w-6 h-6" />
                </button>
              </div>
            </div>

            {salary?.approved && (
              <div className="flex items-center justify-center mt-3">
                <Button
                  onClick={downloadPdf}
                  variant="outline"
                  disabled={loading}
                >
                  Download Salary Slip
                </Button>
              </div>
            )}

            <div className="p-5">
              <div id="pdf-container" className="max-w-6xl mx-auto">
                <div className="border shadow p-4 rounded bg-white text-[#09090b]">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="flex flex-row justify-between gap-2 items-center w-full">
                      <div className="relative w-28 h-auto">
                        <img
                          src={SaasakiLogo}
                          alt="Saasaki Logo"
                          className="max-w-full w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex flex-col gap-2 text-right">
                        <p className="text-lg font-semibold">
                          SAASAKI INFOTECH PRIVATE LIMITED
                        </p>
                        <p className="max-w-lg text-sm font-medium">
                          OFFICE-608, KAMDHENU COMMERZ, KHARGHAR, PANVEL
                          NAVIMUMBAI Raigarh MH 410210 IN
                        </p>
                        <p className="max-w-lg text-sm font-medium mb-3">
                          (info@saasaki.com)
                        </p>
                      </div>
                    </div>

                    <h1 className="text-center text-2xl font-bold">
                      Salary Slip
                    </h1>

                    <div className="h-[6px] max-w-40 w-full bg-emerald-500" />
                  </div>

                  <div className="mt-5">
                    <h4 className="text-lg font-semibold underline underline-offset-4">
                      Employee Details:
                    </h4>
                    <div className="grid grid-cols-3 mt-2 gap-x-8 gap-y-1">
                      <div className="flex items-center">
                        <div>Employee Code</div>
                        <div className="flex-1 px-4 font-semibold">
                          {data?.user?.employeeCode}
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div>Employee Name</div>
                        <div className="flex-1 px-4 font-semibold">
                          {data?.user?.name}
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div>Designation</div>
                        <div className="flex-1 px-4 font-semibold">
                          {data?.user?.role}
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div>Pay Period</div>
                        <div className="flex-1 px-4 font-semibold">
                          {salary?.month}, {salary?.year}
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div>PF Number</div>
                        <div className="flex-1 px-4 font-semibold">
                          {data?.user?.pfNumber}
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div>PAN</div>
                        <div className="flex-1 px-4 font-semibold">
                          {data?.user?.panNumber}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-1 flex gap-8">
                    <div className="flex-1 py-3">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Earnings</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {earningComponents?.map((ec, index) => {
                            return (
                              <TableRow key={index} className="text-[14px]">
                                <TableCell className="p-1">
                                  {ec?.name}
                                </TableCell>
                                <TableCell className="text-right p-1">
                                  {ec?.amount}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="flex-1 py-3">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Deduction</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deductionComponents?.map((ec, index) => {
                            return (
                              <TableRow key={index} className="text-[14px]">
                                <TableCell className="p-1">
                                  {ec?.name}
                                </TableCell>
                                <TableCell className="text-right p-1">
                                  {ec?.amount}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-4 gap-1">
                    <div className="flex flex-row items-center gap-3">
                      <span className="font-semibold">
                        Total Days in month:{" "}
                      </span>
                      <span>{salary?.daysInMonth}</span>
                    </div>
                    <div className="flex flex-row items-center gap-3">
                      <span className="font-semibold">Payable Days: </span>
                      <span>{salary?.daysPayable}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    <div className="flex flex-row items-center gap-3">
                      <span className="font-semibold">Gross Salary: </span>
                      <span>{data?.user?.grossSalary}</span>
                    </div>
                    <div className="flex flex-row items-center gap-3">
                      <span className="font-semibold">Total Earnings: </span>
                      <span>{salary?.totalEarnings}</span>
                    </div>
                    <div className="flex flex-row items-center gap-3">
                      <span className="font-semibold">Total Deductions: </span>
                      <span>{salary?.totalDeductions}</span>
                    </div>
                    <div className="flex flex-row items-center justify-center gap-3 border shadow-md rounded-xl p-3 text-2xl">
                      <span className="font-semibold">Net Pay: </span>
                      <span>{salary?.netPay}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
